import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Palette, Upload, Eye, Check, Globe, ShieldCheck, RefreshCw, Copy } from 'lucide-react';
import { branding as brandApi } from '@/lib/api';
import { safeHtml } from '@/lib/sanitize';
import { toast } from 'sonner';

export default function BrandingCard() {
  const [b, setB] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [previewHtml, setPreviewHtml] = React.useState('');
  const [showPreview, setShowPreview] = React.useState(false);
  const fileInput = React.useRef(null);

  const load = React.useCallback(() => brandApi.get().then(setB), []);
  React.useEffect(() => { load(); }, [load]);

  const patch = (k, v) => setB((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      const clean = { ...b };
      delete clean.logo_url;
      const r = await brandApi.save(clean);
      setB(r);
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
    } finally { setSaving(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const r = await brandApi.uploadLogo(file);
      setB(r);
    } finally { setUploading(false); }
  };

  const openPreview = async () => {
    const r = await brandApi.preview();
    setPreviewHtml(r.html);
    setShowPreview(true);
  };

  if (!b) return <Card><CardContent className="p-6 text-sm text-slate-500"><Loader2 className="inline h-4 w-4 animate-spin" /> Loading branding…</CardContent></Card>;

  return (
    <Card className="border-slate-200" data-testid="branding-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4 text-[#2ECC9A]" />
            <div className="text-sm font-semibold text-[#1A3A5C]">Firm branding</div>
            {saved && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]"><Check className="mr-1 h-3 w-3"/> Saved</Badge>}
          </div>
          <Button data-testid="branding-preview" variant="outline" size="sm" onClick={openPreview}
                  className="h-8 gap-1 text-xs">
            <Eye className="h-3 w-3" /> Preview email
          </Button>
        </div>

        <p className="mt-2 text-xs text-slate-500">
          Every outbound email (receipts, invites, Q&amp;A replies, weekly digest,
          magic-link) and PDF (letters, pathway snapshots, receipts) uses these values
          instead of the default ImmiPro branding.
        </p>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          {/* Logo uploader */}
          <div className="md:col-span-2 flex items-center gap-4 rounded-md border border-dashed border-slate-300 bg-slate-50 p-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-md border border-slate-200 bg-white">
              {b.logo_url
                ? <img data-testid="branding-logo-img" src={b.logo_url} alt="logo"
                       className="max-h-14 max-w-14 object-contain" />
                : <div className="text-[10px] uppercase tracking-widest text-slate-400">No logo</div>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-[#1A3A5C]">Firm logo</div>
              <div className="text-[11px] text-slate-500">PNG, JPG, SVG, or WebP · up to 2 MB · appears in every email + PDF header</div>
            </div>
            <input ref={fileInput} type="file" data-testid="branding-logo-input"
                   accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden"
                   onChange={(e) => upload(e.target.files?.[0])} />
            <Button data-testid="branding-logo-upload" variant="outline" size="sm"
                    onClick={() => fileInput.current?.click()} disabled={uploading}
                    className="h-8 gap-1 text-xs">
              {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
              {b.logo_url ? 'Replace' : 'Upload'}
            </Button>
          </div>

          <div>
            <Label className="text-xs">Firm display name</Label>
            <Input data-testid="branding-firm-name" value={b.firm_display_name || ''}
                   onChange={(e) => patch('firm_display_name', e.target.value)} className="mt-1 text-xs" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Primary color</Label>
              <div className="mt-1 flex items-center gap-2">
                <input data-testid="branding-primary-color" type="color" value={b.primary_color || '#1A3A5C'}
                       onChange={(e) => patch('primary_color', e.target.value)}
                       className="h-9 w-12 rounded border border-slate-200" />
                <Input value={b.primary_color || ''} onChange={(e) => patch('primary_color', e.target.value)}
                       className="font-mono text-xs" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Accent color</Label>
              <div className="mt-1 flex items-center gap-2">
                <input data-testid="branding-accent-color" type="color" value={b.accent_color || '#2ECC9A'}
                       onChange={(e) => patch('accent_color', e.target.value)}
                       className="h-9 w-12 rounded border border-slate-200" />
                <Input value={b.accent_color || ''} onChange={(e) => patch('accent_color', e.target.value)}
                       className="font-mono text-xs" />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs">MARN (Migration Agents Registration Number)</Label>
            <Input data-testid="branding-marn" value={b.marn || ''} onChange={(e) => patch('marn', e.target.value)} placeholder="e.g. 1876543" className="mt-1 font-mono text-xs" />
          </div>
          <div>
            <Label className="text-xs">ABN</Label>
            <Input data-testid="branding-abn" value={b.abn || ''} onChange={(e) => patch('abn', e.target.value)} placeholder="e.g. 51 824 753 556" className="mt-1 font-mono text-xs" />
          </div>
          <div>
            <Label className="text-xs">Phone</Label>
            <Input data-testid="branding-phone" value={b.phone || ''} onChange={(e) => patch('phone', e.target.value)} placeholder="+61 2 9000 1234" className="mt-1 text-xs" />
          </div>
          <div>
            <Label className="text-xs">Website</Label>
            <Input data-testid="branding-website" value={b.website || ''} onChange={(e) => patch('website', e.target.value)} placeholder="firm.com.au" className="mt-1 text-xs" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Address (appears in email footer)</Label>
            <Input data-testid="branding-address" value={b.address || ''} onChange={(e) => patch('address', e.target.value)} placeholder="Level 12, 60 Margaret Street, Sydney NSW 2000" className="mt-1 text-xs" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Letterhead footer disclaimer</Label>
            <textarea data-testid="branding-footer" value={b.letterhead_footer || ''}
                      onChange={(e) => patch('letterhead_footer', e.target.value)} rows={2}
                      className="mt-1 w-full rounded-md border border-slate-200 bg-white p-2 text-xs" />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Button data-testid="branding-save" onClick={save} disabled={saving}
                  className="h-9 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs">
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save branding'}
          </Button>
        </div>

        <ResendDomainWizard />

        {showPreview && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-6"
               onClick={() => setShowPreview(false)}>
            <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-lg bg-white shadow-xl"
                 onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
                <div className="text-sm font-semibold text-[#1A3A5C]">Branded email preview</div>
                <Button variant="outline" size="sm" onClick={() => setShowPreview(false)} className="h-7 text-xs">Close</Button>
              </div>
              <div data-testid="branding-preview-frame" dangerouslySetInnerHTML={{ __html: safeHtml(previewHtml) }} />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ResendDomainWizard() {
  const [state, setState] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [domain, setDomain] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    try { setState(await brandApi.getDomain()); }
    catch (e) { setState({ configured: false, error: e?.response?.data?.detail }); }
    finally { setLoading(false); }
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!/^[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9\-]{0,61}[a-zA-Z0-9])?)+$/.test(domain)) {
      toast.error('Enter a valid domain like nairmigration.com.au'); return;
    }
    setBusy(true);
    try { const s = await brandApi.createDomain(domain); setState(s); toast.success('Domain registered — add the DNS records below'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Domain registration failed'); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const s = await brandApi.verifyDomain();
      setState((prev) => ({ ...(prev || {}), ...s, configured: true }));
      toast.success(`Status: ${s.status}`);
    }
    catch (e) { toast.error(e?.response?.data?.detail || 'Verification failed'); }
    finally { setBusy(false); }
  };

  const statusBadge = () => {
    const s = state?.status;
    const tone = s === 'verified' ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
              : s === 'pending' ? 'border-amber-300 bg-amber-50 text-amber-700'
              : s === 'failed' ? 'border-red-300 bg-red-50 text-red-700'
              : 'border-slate-200 bg-slate-50 text-slate-600';
    return <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tone}`} data-testid="domain-status">{s || 'not configured'}</span>;
  };

  return (
    <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50/30 p-4" data-testid="branding-resend-domain">
      <div className="mb-2 flex items-center gap-2">
        <Globe className="h-4 w-4 text-[#2ECC9A]" />
        <div className="text-sm font-semibold text-[#1A3A5C]">Custom email domain (Resend)</div>
        {state?.configured && statusBadge()}
      </div>
      <p className="mb-3 text-xs text-slate-500">
        Send branded emails from your own domain (e.g. <span className="font-mono">no-reply@yourfirm.com.au</span>) instead of the shared Resend sender.
        Register your domain, add the DNS records shown, then click Verify.
      </p>

      {loading ? (
        <div className="flex justify-center py-2"><Loader2 className="h-4 w-4 animate-spin text-[#2ECC9A]" /></div>
      ) : !state?.configured ? (
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={domain}
            onChange={(e) => setDomain(e.target.value.trim())}
            placeholder="yourfirm.com.au"
            data-testid="domain-input"
            className="h-9 flex-1 min-w-[200px] rounded-md border border-slate-200 bg-white px-2 text-xs"
          />
          <Button size="sm" className="h-9 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90 text-xs"
                  onClick={create} disabled={busy || !domain} data-testid="domain-register">
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Register'}
          </Button>
          {state?.error && <div className="w-full text-xs text-red-600" data-testid="domain-error">{state.error}</div>}
        </div>
      ) : (
        <div>
          <div className="mb-2 flex items-center gap-2 text-xs">
            <span className="font-mono text-[#1A3A5C]">{state.domain}</span>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={verify} disabled={busy} data-testid="domain-verify">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <><RefreshCw className="mr-1 h-3 w-3" /> Verify</>}
            </Button>
          </div>
          {state.records?.length > 0 ? (
            <div className="overflow-x-auto rounded-md border border-slate-200 bg-white" data-testid="domain-records">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Type</th>
                    <th className="px-2 py-1.5 text-left">Name</th>
                    <th className="px-2 py-1.5 text-left">Value</th>
                    <th className="px-2 py-1.5 text-left">Status</th>
                    <th className="px-2 py-1.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {state.records.map((r, i) => (
                    <tr key={i} className="border-t border-slate-100" data-testid={`domain-record-${i}`}>
                      <td className="px-2 py-1.5 font-mono text-[#2ECC9A]">{r.record || r.type}</td>
                      <td className="px-2 py-1.5 font-mono text-slate-700">{r.name}</td>
                      <td className="px-2 py-1.5 font-mono text-slate-700 break-all max-w-[240px]">{r.value}</td>
                      <td className="px-2 py-1.5">
                        <span className={`rounded-full px-1.5 py-0.5 text-[9px] uppercase tracking-wide font-semibold ${
                          r.status === 'verified' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-amber-50 text-amber-700 border border-amber-200'
                        }`}>{r.status || 'pending'}</span>
                      </td>
                      <td className="px-2 py-1.5">
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" title="Copy value"
                                onClick={() => { navigator.clipboard.writeText(r.value || ''); toast.success('Copied'); }}
                                data-testid={`domain-copy-${i}`}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-xs text-slate-500">No DNS records yet — click Verify.</div>
          )}
          {state.note && <div className="mt-2 text-[11px] italic text-slate-500">{state.note}</div>}
          {state.error && <div className="mt-2 text-xs text-red-600" data-testid="domain-live-error">{state.error}</div>}
        </div>
      )}
    </div>
  );
}
